import { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Copy, Share2, Users, Sparkles, Coins, Check, Landmark, ExternalLink, Loader2 } from 'lucide-react';
import { affiliate as affiliateApi } from '../lib/api.js';
import { SITE_URL } from '../lib/config.js';
import { Glass, Button, Tag, Heading, Stat, Empty } from '../components/ui.jsx';

const money = (c) => `$${(c / 100).toFixed(2)}`;

/**
 * The creator's page: their code, the link to share, and what came of it.
 * Only accounts a Keeper made affiliates get anything back from the server.
 */
export default function AffiliatePage() {
  const [ov, setOv] = useState(undefined);      // undefined = loading, null = not an affiliate
  const [copied, setCopied] = useState(null);
  const [params] = useSearchParams();
  const [payBusy, setPayBusy] = useState(false);
  const [payMsg, setPayMsg] = useState(null);

  const load = () => affiliateApi.overview().then(setOv).catch(() => setOv(null));
  useEffect(() => { load(); }, []);
  // Back from Stripe onboarding: ask Stripe what state the account is in now.
  useEffect(() => {
    if (!params.get('stripe')) return;
    affiliateApi.connect('status').then(load).catch(() => {});
  }, [params]);

  const openStripe = async (action) => {
    setPayBusy(true); setPayMsg(null);
    try {
      const { url } = await affiliateApi.connect(action);
      if (url) window.open(url, '_blank');
    } catch (e) {
      setPayMsg(/not set|unavailable|503/i.test(e.message) ? 'Payouts are not switched on yet — a Keeper needs to connect Stripe.' : e.message);
    } finally { setPayBusy(false); }
  };

  const link = ov ? `${SITE_URL}/?ref=${ov.code}` : '';
  const copy = async (what, text) => {
    try { await navigator.clipboard.writeText(text); setCopied(what); setTimeout(() => setCopied(null), 1800); } catch {}
  };
  const share = async () => {
    const text = `Join me — use my invite code ${ov.code} for a welcome bonus.`;
    if (navigator.share) { try { await navigator.share({ title: 'Invite', text, url: link }); } catch {} }
    else copy('link', link);
  };

  return (
    <div className="space-y-4 md:space-y-5">
      <header className="rise rise-1 pt-2 pb-2 md:pb-4">
        <p className="eyebrow">Creator programme</p>
        <h1 className="display text-[2.6rem] md:text-[3.4rem] mt-2">
          Bring your <span className="display-i lit">circle</span>.
        </h1>
      </header>

      {ov === undefined && <Empty>Loading…</Empty>}
      {ov === null && (
        <Glass className="rise rise-2">
          <p className="text-sm leading-relaxed" style={{ color: 'var(--text-2)' }}>
            The creator programme is by invitation. If you have an audience that would love this, get in touch and a Keeper can enable it for your account.
          </p>
        </Glass>
      )}

      {ov && (
        <>
          <Glass className="rise rise-2" style={{ borderColor: 'color-mix(in oklab, var(--gold) 45%, transparent)' }}>
            <Heading eyebrow="Your invite" title={ov.code}
                     action={<Tag tone={ov.enabled ? 'active' : 'expired'} dot>{ov.enabled ? 'Active' : 'Paused'}</Tag>} />
            <p className="text-sm -mt-2 leading-relaxed" style={{ color: 'var(--text-2)' }}>
              Anyone who joins with this code in their first week gets <strong style={{ color: 'var(--text)' }}>{ov.welcome_tokens} welcome tokens</strong>,
              and you earn <strong style={{ color: 'var(--text)' }}>{Number(ov.commission_pct)}%</strong> of everything they ever pay.
            </p>
            <div className="mt-4 glass-flat px-4 py-3 text-sm nums break-all" style={{ color: 'var(--text-2)' }} data-noswipe>{link}</div>
            <div className="mt-3 flex gap-2 flex-wrap">
              <Button variant="gold" onClick={share}><Share2 size={14} /> Share link</Button>
              <Button variant="glass" onClick={() => copy('link', link)}>
                {copied === 'link' ? <Check size={14} /> : <Copy size={14} />} Copy link
              </Button>
              <Button variant="glass" onClick={() => copy('code', ov.code)}>
                {copied === 'code' ? <Check size={14} /> : <Copy size={14} />} Copy code
              </Button>
            </div>
          </Glass>

          <div className="rise rise-3 grid grid-cols-2 md:grid-cols-4 gap-2">
            <Stat label="Invited" value={ov.referred_count} />
            <Stat label="Subscribed" value={ov.subscribed_count} accent="violet" />
            <Stat label="Earned" value={money(ov.earnings_cents)} accent="gold" />
            <Stat label="Last 30 days" value={money(ov.earnings_30d_cents)} />
          </div>

          <Glass className="rise rise-4">
            <Heading eyebrow="Getting paid" title="Your payouts"
                     action={ov.payout_account?.payouts_enabled ? <Tag tone="active" dot>Connected</Tag>
                             : ov.payout_account ? <Tag tone="building" dot>Finish setup</Tag> : null} />
            <p className="text-sm -mt-2 leading-relaxed" style={{ color: 'var(--text-2)' }}>
              You earn {Number(ov.commission_pct)}% of every subscription payment your members make, for as long as they stay.
              Each payment is held for 30 days (the refund window), then paid to your bank on the 1st of the month once you are owed at least $10.
              Stripe handles the bank details and tax forms; we never see them.
            </p>
            <div className="mt-4 grid grid-cols-3 gap-2">
              <Stat label="Payable" value={money(ov.payable_cents)} accent="gold" />
              <Stat label="On hold" value={money(ov.pending_cents)} />
              <Stat label="Paid out" value={money(ov.paid_cents)} accent="violet" />
            </div>
            <div className="mt-4 flex gap-2 flex-wrap items-center" data-noswipe>
              {ov.payout_account?.payouts_enabled ? (
                <Button variant="glass" disabled={payBusy} onClick={() => openStripe('dashboard')}>
                  {payBusy ? <Loader2 size={14} className="animate-spin" /> : <ExternalLink size={14} />} Stripe dashboard
                </Button>
              ) : (
                <Button variant="gold" disabled={payBusy} onClick={() => openStripe('link')}>
                  {payBusy ? <Loader2 size={14} className="animate-spin" /> : <Landmark size={14} />}
                  {ov.payout_account ? 'Continue setup' : 'Set up payouts'}
                </Button>
              )}
              {payMsg && <span className="text-xs" style={{ color: 'var(--text-3)' }}>{payMsg}</span>}
            </div>
            {ov.payouts?.length > 0 && (
              <ul className="mt-4 divide-y text-sm" style={{ borderColor: 'var(--edge)' }}>
                {ov.payouts.map((p, i) => (
                  <li key={i} className="flex items-center justify-between py-2">
                    <span style={{ color: 'var(--text-2)' }}>{new Date(p.at).toLocaleDateString(undefined, { day: 'numeric', month: 'short', year: 'numeric' })}</span>
                    <span className="nums font-semibold" style={{ color: p.status === 'paid' ? 'var(--text)' : 'var(--rust)' }}>{money(p.amount_cents)}</span>
                  </li>
                ))}
              </ul>
            )}
          </Glass>

          <Glass className="rise rise-4">
            <Heading eyebrow="Recent" title="Who came" />
            {ov.recent.length === 0 ? <Empty>No one yet. Share the link where your people are.</Empty> : (
              <ul className="divide-y" style={{ borderColor: 'var(--edge)' }}>
                {ov.recent.map((r, i) => (
                  <li key={i} className="flex items-center justify-between py-2.5 text-sm">
                    <span className="flex items-center gap-2"><Users size={14} style={{ color: 'var(--text-3)' }} /> {r.name}</span>
                    <span className="flex items-center gap-3 text-xs" style={{ color: 'var(--text-3)' }}>
                      {r.subscribed && <Tag tone="active" dot>Subscribed</Tag>}
                      {new Date(r.joined).toLocaleDateString(undefined, { day: 'numeric', month: 'short' })}
                    </span>
                  </li>
                ))}
              </ul>
            )}
          </Glass>

          <p className="rise rise-5 text-xs leading-relaxed max-w-lg px-1" style={{ color: 'var(--text-3)' }}>
            <Sparkles size={11} className="inline mr-1" />Commission is recorded when the store confirms a subscription payment; token packs do not count. <Coins size={11} className="inline mx-1" />Welcome tokens come from the server, never from you.
          </p>
        </>
      )}
    </div>
  );
}
